import React from 'react';
import { FaArrowDown, FaEnvelope, FaPhone, FaBullseye, FaChartLine, FaUsers, FaLightbulb, FaBullhorn, FaCode, FaRocket, FaFire } from 'react-icons/fa';
import './Hero.css';

const Hero = ({ selectedServices = [] }) => {
  const scrollToNext = () => {
    const aboutSection = document.getElementById('about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Determine content based on selected services
  const isDigitalMarketing = selectedServices.includes('digital-marketing');
  const isWebDevelopment = selectedServices.includes('web-development');
  const isBothServices = isDigitalMarketing && isWebDevelopment;

  // Dynamic content configuration
  const getHeroContent = () => {
    if (isBothServices) {
      return {
        subtitle: "Full-Stack Digital Architect & <span class='gradient-text'>Marketing Strategist</span>",
        description: "I don't just build websites—I create complete digital ecosystems that grow your business. From high-converting websites to viral marketing campaigns, I deliver end-to-end solutions that turn visitors into customers and followers into brand advocates.",
        stats: [
          { number: "50+", label: "Websites Built" },
          { number: "27K+", label: "Social Media Reach" },
          { number: "8X", label: "Average Growth" }
        ],
        codeRole: "Full-Stack Growth Hacker",
        codeSkills: ["React", "Marketing", "Analytics", "Design"]
      };
    } else if (isDigitalMarketing) {
      return {
        subtitle: "Social Media Marketing Expert & <span class='gradient-text'>Brand Growth Strategist</span>",
        description: "I don't just manage social media—I architect growth strategies that turn followers into customers. With data-driven campaigns and creative content, I've helped brands achieve viral growth and measurable ROI.",
        stats: [
          { number: "27K+", label: "Monthly Social Reach" },
          { number: "15+", label: "Brand Campaigns" },
          { number: "5X", label: "Average ROI Boost" }
        ],
        codeRole: "Marketing Growth Hacker",
        codeSkills: ["Meta Ads", "Google Ads", "SEO", "Content"]
      };
    } else if (isWebDevelopment) {
      return {
        subtitle: "Full-Stack Developer & <span class='gradient-text'>Digital Solutions Architect</span>",
        description: "I build more than websites—I create digital experiences that convert. From custom web applications to e-commerce platforms, every line of code is written with user experience and business growth in mind.",
        stats: [
          { number: "50+", label: "Websites Built" },
          { number: "3 Years", label: "Development Experience" },
          { number: "100%", label: "Client Satisfaction" }
        ],
        codeRole: "Full-Stack Developer",
        codeSkills: ["React", "Node.js", "PHP", "MySQL"]
      };
    }
    // Fallback to original content
    return {
      subtitle: "Social Media Marketing Expert & <span class='gradient-text'>Brand Growth Strategist</span>",
      description: "I don't just manage social media—I architect growth strategies that turn followers into customers. With technical skills to build what I need, I create complete marketing ecosystems that deliver results.",
      stats: [
        { number: "27K+", label: "Monthly Social Reach" },
        { number: "15+", label: "Brand Campaigns" },
        { number: "5X", label: "Average ROI Boost" }
      ],
      codeRole: "Digital Architect",
      codeSkills: ["React", "Marketing", "AI"]
    };
  };

  const content = getHeroContent();

  return (
    <section id="hero" className="hero-section" role="banner" itemScope itemType="https://schema.org/Person">
      <div className="hero-container">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title" itemProp="name">
              <FaBullseye className="hero-icon" aria-hidden="true" /> <span className="highlight">HARISH KUMAR</span>
            </h1>
            <h2 className="hero-subtitle" itemProp="jobTitle" dangerouslySetInnerHTML={{ __html: content.subtitle }}>
            </h2>
            <p className="hero-description" itemProp="description">
              {content.description}
            </p>
            <div className="hero-stats">
              {content.stats.map((stat, index) => (
                <div key={index} className="stat">
                  <span className="stat-number">{stat.number}</span>
                  <span className="stat-label">{stat.label}</span>
                </div>
              ))}
            </div>
            <div className="hero-cta" role="navigation" aria-label="Main call to action buttons">
              <button 
                className="btn btn-primary" 
                onClick={() => document.getElementById('portfolio').scrollIntoView({ behavior: 'smooth' })}
                aria-label={`Navigate to portfolio section - ${isBothServices ? 'See My Complete Work' : isDigitalMarketing ? 'View Marketing Results' : isWebDevelopment ? 'See Websites I Built' : 'View My Work'}`}
              >
                {isBothServices ? 'See My Complete Work' : isDigitalMarketing ? 'View Marketing Results' : isWebDevelopment ? 'See Websites I Built' : 'View My Work'}
              </button>
              <button 
                className="btn btn-outline" 
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                aria-label={`Navigate to contact section - ${isBothServices ? 'Start Your Project' : isDigitalMarketing ? 'Grow My Brand' : isWebDevelopment ? 'Build My Website' : 'Let\'s Connect'}`}
              >
                {isBothServices ? 'Start Your Project' : isDigitalMarketing ? 'Grow My Brand' : isWebDevelopment ? 'Build My Website' : 'Let\'s Connect'}
              </button>
            </div>
            <div className="hero-contact-info" itemScope itemType="https://schema.org/ContactPoint">
              <div className="contact-item">
                <FaEnvelope aria-hidden="true" />
                <span itemProp="email">
                  <a href="mailto:imdigitalharish@gmail.com" aria-label="Email Harish Kumar">
                    imdigitalharish@gmail.com
                  </a>
                </span>
              </div>
              <div className="contact-item">
                <FaPhone aria-hidden="true" />
                <span itemProp="telephone">
                  <a href="tel:+919602003790" aria-label="Call Harish Kumar">
                    +91 96020 03790
                  </a>
                </span>
              </div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-card-container">
              <div className="hero-card">
                <div className="card-content">
                  <div className="code-snippet">
                    <div className="code-line">
                      <span className="code-keyword">const</span> <span className="code-variable">harish</span> = {'{'}
                    </div>
                    <div className="code-line indent">
                      <span className="code-property">role</span>: <span className="code-string">"{content.codeRole}"</span>,
                    </div>
                    <div className="code-line indent">
                      <span className="code-property">skills</span>: [
                      {content.codeSkills.map((skill, index) => (
                        <span key={skill}>
                          <span className="code-string">"{skill}"</span>
                          {index < content.codeSkills.length - 1 && ', '}
                        </span>
                      ))}
                      ],
                    </div>
                    <div className="code-line indent">
                      <span className="code-property">passion</span>: <span className="code-string">"Building & Growing Brands"</span>
                    </div>
                    <div className="code-line">{'};'}</div>
                  </div>
                </div>
              </div>
              <FaChartLine className="floating-icon icon-1" />
              <FaUsers className="floating-icon icon-2" />
              <FaLightbulb className="floating-icon icon-3" />
              <FaBullhorn className="floating-icon icon-4" />
            </div>
          </div>
        </div>
        <div className="scroll-indicator" onClick={scrollToNext}>
          <FaArrowDown />
        </div>
      </div>
    </section>
  );
};

export default Hero;
